import { useEffect } from "react";
import { Navigate, Outlet, useLocation, useParams } from "react-router-dom";

import CreateCampaignModal from "common/components/CreateCampaignModal";
import EmptyPlaceholder from "common/components/EmptyPlaceholder";
import Loader from "common/components/Loader";
import {
  SelectedWorkspaceProvider,
  useSelectedWorkspaceContext,
} from "common/context/SelectedWorkspaceContext";
import { UnreadMessagesWebsocketProvider } from "common/context/UnreadMessagesWebsocketContext";
import useWorkspaces from "common/datahooks/useWorkspaces";
import { getWhitelabelCustomization } from "common/helpers/utils";
import useAppStore from "common/hooks/useAppStore";
import useGTM from "common/hooks/useGTM";
import useNavItems from "common/hooks/useNavItems";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import useWorkspaceChangeUpdates from "common/hooks/useWorkspaceChangeUpdates";
import DesktopLayout from "common/layouts/WorkspaceLayout/DesktopLayout";
import MobileLayout from "common/layouts/WorkspaceLayout/MobileLayout";

import { workspaceRoutes } from "../../../pages/routes";
import { ContentContainer } from ".";
import WebPushPrompt from "./WebPushPrompt";

import NotFoundImage from "assets/images/404.png";

function Content({
  isLoading,
  errorComponent,
}: {
  isLoading: boolean;
  errorComponent: JSX.Element;
}) {
  useWorkspaceChangeUpdates();
  const { sendEvent } = useGTM();
  const isDesktop = useTwBreakpoint("lg");
  const location = useLocation();
  const workspace = useSelectedWorkspaceContext();
  const navItems = useNavItems();
  const isCampaignModalOpen = useAppStore((state) => state.isCampaignModalOpen);
  const setIsCampaignModalOpen = useAppStore(
    (state) => state.setIsCampaignModalOpen,
  );

  const isSettingsPage = location.pathname.includes("settings");

  useEffect(() => {
    if (!isSettingsPage) {
      useAppStore.getState().setPreviousLocation(location);
    }

    window.scrollTo({ top: 0 });

    const pagePath = workspace
      ? location.pathname.replace(`/workspaces/${workspace.id}`, "")
      : location.pathname;

    if (pagePath) {
      sendEvent("virtualPageView", () => ({
        pageUrl: window.location.href,
        pagePath,
      }));
    }
  }, [location]);

  let content = <Outlet />;

  const currentRoute = workspaceRoutes.find(({ path }) =>
    location.pathname.includes(path.replace("/*", "").split("/")[0]),
  );

  if (errorComponent) {
    content = <ContentContainer>{errorComponent}</ContentContainer>;
  } else if (isLoading) {
    content = (
      <ContentContainer>
        <Loader />
      </ContentContainer>
    );
  } else if (
    currentRoute?.visibleForPermissions &&
    !currentRoute.visibleForPermissions.some((permission) =>
      workspace.permissions.includes(permission),
    )
  ) {
    content = (
      <ContentContainer>
        <EmptyPlaceholder
          title="Page not found"
          subtitle="The page you are looking for is not available"
          imageSrc={NotFoundImage}
        />
      </ContentContainer>
    );
  }

  let isLocked = false;
  if (!isSettingsPage) {
    const {
      subscription_status: subscriptionStatus,
      agency_license_count,
      lifetime_license_count: lifetimeLicenseCount,
      rental_subscription_status,
    } = workspace;

    isLocked =
      !getWhitelabelCustomization() &&
      lifetimeLicenseCount === 0 &&
      agency_license_count === 0 &&
      (!rental_subscription_status ||
        rental_subscription_status === "cancelled") &&
      (!subscriptionStatus || subscriptionStatus === "cancelled");
  }

  const LayoutComponent = isDesktop ? DesktopLayout : MobileLayout;

  return (
    <>
      <LayoutComponent
        navItems={navItems}
        workspace={workspace}
        isLocked={isLocked}
      >
        {content}
      </LayoutComponent>

      <WebPushPrompt />

      {workspace && (
        <CreateCampaignModal
          isOpen={isCampaignModalOpen}
          onClose={() => setIsCampaignModalOpen(false)}
        />
      )}
    </>
  );
}

export default function Layout() {
  const { workspaceId } = useParams();
  const { workspaces } = useWorkspaces();
  const location = useLocation();

  let contextWorkspaceId: string;

  if (getWhitelabelCustomization()) {
    contextWorkspaceId = workspaces[0].id;
  } else {
    const selectedWorkspace = workspaces.find(({ id }) => id === workspaceId);

    // Validate the workspaceId in the url
    if (!workspaceId || !selectedWorkspace) {
      return <Navigate to={`/${workspaces[0].id}`} replace />;
    }

    // Check if the current location matches the base workspace URL and redirect to "/home"
    if (location.pathname === `/workspaces/${workspaceId}`) {
      return (
        <Navigate
          to={`/workspaces/${workspaceId}/${workspaceRoutes[0].path}`}
          replace
        />
      );
    }

    contextWorkspaceId = workspaceId;
  }

  return (
    <SelectedWorkspaceProvider workspaceId={contextWorkspaceId}>
      {(props) => (
        <UnreadMessagesWebsocketProvider>
          <Content {...props} />
        </UnreadMessagesWebsocketProvider>
      )}
    </SelectedWorkspaceProvider>
  );
}
